import React, { useState, useEffect } from "react";
import { CiMenuKebab } from "react-icons/ci";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../../../components/Constant/Notification";
import ProspectPropertyService from "../../../../../Service/ProspectPropertyService";
import UserService from "../../../../../../GlobalSetupComponents/Services/UserService";
import {
  formatDate,
  formatDateForInput,
} from "../../../../../../utils/dateFormatter";
import { userUid } from "../../../../../../utils/storage";
import {
  Divider,
  MenuItem,
} from "../../../../../../components/Constant/Constant";
import CustomSidebar from "../../../../../../components/common/CustomSidebar";
import MediaUpload from "../../../../../../components/SetupComponents/PropertiesComponents/MediaUpload";
import AsteriskStyle from "../../../../../../components/Form/Tooltip/AsteriskStyle";

const AdvocateAnalysis = ({
  propertyId,
  setPropertyData,
  propertyData,
  assignedUserData,
  setTriggerFetch,
  isDropped
}) => {
  const [isLegalAnalysisModalOpen, setIsLegalAnalysisModalOpen] = useState(false);
  const [isFinalOpinionModalOpen, setIsFinalOpinionModalOpen] = useState(false);
  const [isResponseModalOpen, setIsResponseModalOpen] = useState(false);

  // State for legal and final opinions
  const [legalOpinions, setLegalOpinions] = useState([]);
  const [finalOpinions, setFinalOpinions] = useState([]);

  // Form states
  const [legalOpinionForm, setLegalOpinionForm] = useState({
    files: [],
    notes: "",
  });
  const [finalOpinionForm, setFinalOpinionForm] = useState({
    files: [],
    notes: "",
  });
  const [responseForm, setResponseForm] = useState({
    files: [],
    description: "",
    respondingTo: null,
  });

  // Validation states
  const [formErrors, setFormErrors] = useState({});

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmittingLegal, setIsSubmittingLegal] = useState(false);
  const [isSubmittingFinal, setIsSubmittingFinal] = useState(false);
  const [isSubmittingResponse, setIsSubmittingResponse] = useState(false);

  // Users for display
  const [users, setUsers] = useState([]);

  // Add triggerFetch state to properly handle refreshes
  const [triggerFetch, setInternalTriggerFetch] = useState(false);

  // Load users and opinions on component mount or when propertyId/triggerFetch changes
  useEffect(() => {
    if (propertyId) {
     
      loadOpinions();
    }
  }, [propertyId, triggerFetch]);

  // External trigger effect
  useEffect(() => {
    if (setTriggerFetch) {
      setInternalTriggerFetch(prev => !prev);
    }
  }, [setTriggerFetch]);

  

  const loadOpinions = async () => {
    try {
      const response = await ProspectPropertyService.getStageWiseProperties({
        propertyId: propertyId,
        stage: "Analysis",
      });
      const data = response.data?.data;
      if (data && data.advocateAnalysisRecords) {
        const { legalOpinions, finalOpinions } = data.advocateAnalysisRecords;
        setLegalOpinions(
          legalOpinions.map((opinion) => ({
            id: opinion.id,
            files: [{ url: opinion.fileUrl, name: `Document ${opinion.id}` }],
            notes: opinion.notes,
            date: opinion.createdTimestamp,
            user: opinion.createdBy.id,
            userName: opinion.createdBy.name,
            userRole : opinion.createdBy.role,
            hasResponse: opinion.hasResponse,
            responses: opinion.hasResponse
              ? [
                  {
                    id: opinion.response?.responseTimestamp || `response-${opinion.id}`,
                    description: opinion.response?.description || "",
                    files: opinion.response?.fileUrl
                      ? [{ url: opinion.response.fileUrl, name: `Response Document` }]
                      : [],
                    date: opinion.response?.responseTimestamp || "",
                    userName: opinion.response?.respondedBy?.name || "",
                    userId: opinion.response?.respondedBy?.id || "",
                    userRole: opinion.response?.respondedBy?.role || "",
                  },
                ]
              : [],
          }))
        );
        setFinalOpinions(
          finalOpinions.map((opinion) => ({
            id: opinion.id,
            files: [{ url: opinion.fileUrl, name: `Final Document ${opinion.id}` }],
            notes: opinion.notes,
            date: opinion.createdTimestamp,
            userName: opinion.createdBy.name,
            userRole : opinion.createdBy.role,
            userId: opinion.createdBy.id,
          }))
        );
        setPropertyData(data); 
      }
    } catch (error) {
      console.error("Error loading opinions:", error);
     
    }
  };

  // Validation functions
  const validateForm = (formType, formData) => {
    const errors = {};

    if (!formData.files || formData.files.length === 0) {
      errors.files = "At least one file is required";
    }

    if (
      formType === "response" &&
      (!formData.description || formData.description.trim() === "")
    ) {
      errors.description = "Response description is required";
    }

    if (
      formType === "legal" &&
      (!formData.notes || formData.notes.trim() === "")
    ) {
      errors.notes = "Notes are required";
    }

    return errors;
  };

  // Check if final opinion can be added
  const canAddFinalOpinion = () => {
    return legalOpinions.every((opinion) => opinion.hasResponse) && finalOpinions.length === 0;
  };

  // Check if legal opinion can be added (don't allow if there are pending responses or final opinion exists)
  const canAddLegalOpinion = () => {
    return legalOpinions.every((opinion) => opinion.hasResponse) && finalOpinions.length === 0;
  };

  // Form submission handlers
  const handleLegalOpinionSubmit = async () => {
    const errors = validateForm("legal", legalOpinionForm);
    setFormErrors(errors);

    if (Object.keys(errors).length === 0) {
      setIsSubmittingLegal(true);
      try {
        const body = {
          propertyId: propertyId,
          updatedBy: userUid,
          type: "Legal Opinion",
          fileUrl: legalOpinionForm.files[0]?.url || legalOpinionForm.files[0] || null,
          notes: legalOpinionForm.notes,
        };

        const response = await ProspectPropertyService.analysisUpdateOpinions(body);

        if (response.data) {
          showSuccessToast("Legal opinion added successfully");
          setLegalOpinionForm({ files: [], notes: "" });
          setIsLegalAnalysisModalOpen(false);
          setFormErrors({});
          setInternalTriggerFetch(prev => !prev);
          if (setTriggerFetch) setTriggerFetch(prev => !prev);
        } else {
          console.error("Error submitting legal opinion:");
          showErrorToast("Failed to add legal opinion");
        }
      } catch (error) {
        console.error("Error submitting legal opinion:", error);
        showErrorToast("Failed to add legal opinion");
      } finally {
        setIsSubmittingLegal(false);
      }
    }
  };

  const handleFinalOpinionSubmit = async () => {
    if (!canAddFinalOpinion()) {
      if (finalOpinions.length > 0) {
        showErrorToast("Final opinion already exists. Only one final opinion is allowed.");
      } else {
        showErrorToast("Cannot add final opinion until all legal opinions have responses");
      }
      return;
    }

    const errors = validateForm("final", finalOpinionForm);
    setFormErrors(errors);

    if (Object.keys(errors).length === 0) {
      setIsSubmittingFinal(true);
      try {
        const body = {
          propertyId: propertyId,
          updatedBy: userUid,
          type: "Final Opinion",
          fileUrl: finalOpinionForm.files[0]?.url || finalOpinionForm.files[0] || null,
          notes: finalOpinionForm.notes,
        };

        const response = await ProspectPropertyService.analysisUpdateOpinions(body);

        if (response.data) {
          showSuccessToast("Final opinion added successfully");
          setFinalOpinionForm({ files: [], notes: "" });
          setIsFinalOpinionModalOpen(false);
          setFormErrors({});
          setInternalTriggerFetch(prev => !prev);
          if (setTriggerFetch) setTriggerFetch(prev => !prev);
        } else {
          showErrorToast("Failed to add final opinion");
        }
      } catch (error) {
        console.error("Error submitting final opinion:", error);
        showErrorToast("Failed to add final opinion");
      } finally {
        setIsSubmittingFinal(false);
      }
    }
  };

  const handleResponseSubmit = async () => {
    const errors = validateForm("response", responseForm);
    setFormErrors(errors);

    if (Object.keys(errors).length === 0 && responseForm.respondingTo) {
      setIsSubmittingResponse(true);
      try {
        const body = {
          analysisId: responseForm.respondingTo,
          propertyId: propertyId,
          responseDescription: responseForm.description,
          responseFileUrl: responseForm.files[0]?.url || responseForm.files[0] || null,
          respondedBy: userUid,
        };

        const response = await ProspectPropertyService.analysisUpdateResponseAdd(body);

        if (response.data) {
          showSuccessToast("Response added successfully");
          setResponseForm({ files: [], description: "", respondingTo: null });
          setIsResponseModalOpen(false);
          setFormErrors({});
          setInternalTriggerFetch(prev => !prev);
          if (setTriggerFetch) setTriggerFetch(prev => !prev);
        } else {
          showErrorToast("Failed to add response");
        }
      } catch (error) {
        console.error("Error submitting response:", error);
        showErrorToast("Failed to add response");
      } finally {
        setIsSubmittingResponse(false);
      }
    }
  };

  const openResponseModal = (opinionId) => {
    setResponseForm((prev) => ({ ...prev, respondingTo: opinionId }));
    setIsResponseModalOpen(true);
  };

  const getUserName = (userId) => {
    const user = users.find((u) => u.uid === userId);
    return user ? user.name : "Unknown User";
  };

  return (
    <div className="overflow-hidden">
      <div className="flex flex-col">
        <div className="flex justify-between gap-7 items-center">
          <div className="text-sm flex flex-col w-full border border-gray-300 p-3 rounded-[10px] justify-between text-slate-500">
            <div>
              Assigned Front end person -{" "}
              <span className="text-blue-500">{assignedUserData[1]?.name || "N/A"}</span>
            </div>
            <div>
              Advocate name -{" "}
              <span className="text-blue-500">{assignedUserData[0]?.name || "N/A"}</span>
            </div>
          </div>

          <div className="w-full flex gap-4">
            <button
              disabled={isDropped || !canAddLegalOpinion()}
              onClick={() => {
                if (canAddLegalOpinion()) {
                  setIsLegalAnalysisModalOpen(true);
                } else if (finalOpinions.length > 0) {
                  showErrorToast("Cannot add legal opinions when final opinion already exists");
                } else {
                  showErrorToast("All existing legal opinions must have responses before adding a new one");
                }
              }}
              className={`flex items-center border border-gray-300 p-3 rounded-[10px] justify-center text-white transition-colors ${
                canAddLegalOpinion() && !isDropped ? 'bg-customGreen hover:bg-green-600' : 'bg-gray-400 cursor-not-allowed'
              }`}
            >
              <span>Add Legal Opinion</span>
            </button>

            <button
              disabled={isDropped || !canAddFinalOpinion()}
              onClick={() => {
                if (canAddFinalOpinion()) {
                  setIsFinalOpinionModalOpen(true);
                } else if (finalOpinions.length > 0) {
                  showErrorToast("Final opinion already exists. Only one final opinion is allowed.");
                } else {
                  showErrorToast("All legal opinions must have responses before adding a final opinion");
                }
              }}
              className={`flex items-center border border-gray-300 p-3 rounded-[10px] justify-center text-white transition-colors ${
                canAddFinalOpinion() && !isDropped ? "bg-customGreen hover:bg-green-600" : "bg-gray-400 cursor-not-allowed"
              }`}
            >
              <span>Add Final Opinion</span>
            </button>
          </div>
        </div>

        {/* Legal Opinions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-4 max-h-[450px] overflow-y-auto pr-2">
          {legalOpinions.map((opinion, index) => (
            <div
              key={opinion.id}
              className="flex flex-col gap-4 border border-gray-300 p-4 rounded-[10px] min-h-fit shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="text-[12px] font-medium text-customGreen">
                {index + 1}
                {index === 0 ? "st" : index === 1 ? "nd" : index === 2 ? "rd" : "th"} Legal Opinion

                <div>
                  {(opinion.date) && ` (${(opinion.date)})`}, By {opinion?.userName || ""} ({opinion?.userRole || ""})
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {opinion.files.map((file, fileIndex) => (
                  <div
                    key={fileIndex}
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
                  >
                    <span className="text-sm">Document {fileIndex + 1}</span>
                    <a href={file.url} target="_blank" rel="noopener noreferrer" className="text-xs underline">
                      View
                    </a>
                  </div>
                ))}
              </div>

              {opinion.notes && (
                <div title={opinion?.notes} className="text-sm text-wrap truncate line-clamp-2  text-gray-600">
                  <span className="font-medium">Notes:</span> {opinion.notes}
                </div>
              )}

              {!opinion.hasResponse && finalOpinions.length === 0 && (
                <button
                  disabled={isDropped}
                  onClick={() => openResponseModal(opinion.id)}
                  className={`self-start px-4 py-2 rounded-full transition-colors text-sm ${
                    isDropped ? 'bg-gray-400 cursor-not-allowed text-white' : 'bg-[#E29A47] text-white hover:bg-[#D18B3A]'
                  }`}
                >
                  Add Response
                </button>
              )}

              {/* Responses for this opinion */}
              {opinion.responses.map((response, responseIndex) => (
                <div
                  key={response.id}
                  className=""
                >
                  <div className=" text-[11px] text-gray-700">
                    <span className="text-customGreen font-medium text-[13px]">Response {responseIndex + 1} </span>{(response.date)}, By {response?.userName || ""} ({response?.userRole || ""})
                    
                  </div>
                  <div title={response?.description} className="text-sm cursor-pointer text-wrap truncate line-clamp-2 text-[11px] text-gray-600 mt-1">
                    <span className="font-medium">Description:</span> {response.description}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {response.files.map((file, fileIndex) => (
                      <div key={fileIndex} className="flex items-center gap-2">
                        <span className="text-sm">Document {fileIndex + 1}</span>
                        <a
                          href={file.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs underline"
                        >
                          View
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}

          {/* Final Opinions */}
          {finalOpinions.map((finalOpinion, index) => (
            <div
              key={finalOpinion.id}
              className="flex flex-col gap-4 border border-green-300 p-4 rounded-[10px] mb-4 bg-green-50"
            >
              <div className="text-[12px] font-medium text-customGreen">
                Final Opinion {index + 1} {(finalOpinion.date)}, By {finalOpinion?.userName || ""} ({finalOpinion?.userRole || ""})
            
              </div>

              <div className="flex flex-wrap gap-2">
                {finalOpinion.files.map((file, fileIndex) => (
                  <div
                    key={fileIndex}
                    className="flex items-center gap-2 text-green-600 hover:text-green-800"
                  >
                    <span className="text-sm">Document {fileIndex + 1}</span>
                    <a href={file.url} target="_blank" rel="noopener noreferrer" className="text-xs underline">
                      View
                    </a>
                  </div>
                ))}
              </div>

              {finalOpinion.notes && (
                <div title={finalOpinion?.notes} className="text-sm text-wrap cursor-pointer truncate line-clamp-3 text-gray-600">
                  <span className="font-medium">Notes:</span> {finalOpinion.notes}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Empty state */}
        {legalOpinions.length === 0 && finalOpinions.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <p>No legal analysis added yet.</p>
            <p className="text-sm">Click "Add Legal Opinion" to get started.</p>
          </div>
        )}
      </div>

      <CustomSidebar
        visible={isLegalAnalysisModalOpen}
        onHide={() => {
          setIsLegalAnalysisModalOpen(false);
          setLegalOpinionForm({ files: [], notes: "" });
          setFormErrors({});
        }}
        onClose={() => {}}
        title="Add Legal Opinion"
      >
        <div className="p-4">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Upload File <AsteriskStyle />
            </label>
            <MediaUpload
              mediaType={"document"}
              files={legalOpinionForm.files}
              onChange={(files) =>
                setLegalOpinionForm((prev) => ({ ...prev, files }))
              }
              accept="application/pdf,application/dwg,application/x-dwg,application/dxf,application/x-dxf"
              maxFiles={1}
              folderpath="propertyAttachments"
            />
            {formErrors.files && (
              <p className="text-red-500 text-xs mt-1">{formErrors.files}</p>
            )}
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes <AsteriskStyle />
            </label>
            <textarea
              value={legalOpinionForm.notes}
              onChange={(e) =>
                setLegalOpinionForm((prev) => ({
                  ...prev,
                  notes: e.target.value,
                }))
              }
              className={`w-full border rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-customGreen ${
                formErrors.notes ? "border-red-500" : "border-gray-300"
              }`}
              rows={4}
              placeholder="Enter analysis notes..."
            />
            {formErrors.notes && (
              <p className="text-red-500 text-xs mt-1">{formErrors.notes}</p>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <button
              onClick={() => {
                setIsLegalAnalysisModalOpen(false);
                setLegalOpinionForm({ files: [], notes: "" });
                setFormErrors({});
              }}
              className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 focus:outline-none"
            >
              Cancel
            </button>
            <button
              onClick={handleLegalOpinionSubmit}
              disabled={isSubmittingLegal || isDropped}
              className={`px-4 py-2 rounded-md focus:outline-none text-white ${
                isSubmittingLegal || isDropped
                  ? "bg-gray-400 cursor-not-allowed" 
                  : "bg-customGreen hover:bg-green-600"
              }`}
            >
              {isSubmittingLegal ? "Adding..." : "Add Opinion"}
            </button>
          </div>
        </div>
      </CustomSidebar>

      <CustomSidebar
        visible={isFinalOpinionModalOpen}
        onHide={() => {
          setIsFinalOpinionModalOpen(false);
          setFinalOpinionForm({ files: [], notes: "" });
          setFormErrors({});
        }}
        onClose={() => {}}
        title="Add Final Opinion"
      >
        <div className="p-4">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Upload File <AsteriskStyle />
            </label>
            <MediaUpload
              mediaType={"document"}
              files={finalOpinionForm.files}
              onChange={(files) =>
                setFinalOpinionForm((prev) => ({ ...prev, files }))
              }
              accept="application/pdf,application/dwg,application/x-dwg,application/dxf,application/x-dxf"
              maxFiles={1}
              folderpath="propertyAttachments"
            />
            {formErrors.files && (
              <p className="text-red-500 text-xs mt-1">{formErrors.files}</p>
            )}
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={finalOpinionForm.notes}
              onChange={(e) =>
                setFinalOpinionForm((prev) => ({
                  ...prev,
                  notes: e.target.value,
                }))
              }
              className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-customGreen"
              rows={4}
              placeholder="Enter final opinion notes..."
            />
          </div>

          <div className="flex justify-end gap-2">
            <button
              onClick={() => {
                setIsFinalOpinionModalOpen(false);
                setFinalOpinionForm({ files: [], notes: "" });
                setFormErrors({});
              }}
              className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 focus:outline-none"
            >
              Cancel
            </button>
            <button
              onClick={handleFinalOpinionSubmit}
              disabled={isSubmittingFinal || isDropped}
              className={`px-4 py-2 rounded-md focus:outline-none text-white ${
                isSubmittingFinal || isDropped
                  ? "bg-gray-400 cursor-not-allowed" 
                  : "bg-customGreen hover:bg-green-600"
              }`}
            >
              {isSubmittingFinal ? "Adding..." : "Add Final Opinion"}
            </button>
          </div>
        </div>
      </CustomSidebar>

      <CustomSidebar
        visible={isResponseModalOpen}
        onHide={() => {
          setIsResponseModalOpen(false);
          setResponseForm({ files: [], description: "", respondingTo: null });
          setFormErrors({});
        }}
        onClose={() => {}}
        title="Add Response to Legal Opinion"
      >
        <div className="p-4">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Upload File <AsteriskStyle />
            </label>
            <MediaUpload
              mediaType={"document"}
              files={responseForm.files}
              onChange={(files) =>
                setResponseForm((prev) => ({ ...prev, files }))
              }
              accept="application/pdf,application/dwg,application/x-dwg,application/dxf,application/x-dxf"
              maxFiles={1}
              folderpath="propertyAttachments"
            />
            {formErrors.files && (
              <p className="text-red-500 text-xs mt-1">{formErrors.files}</p>
            )}
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Response Description <AsteriskStyle />
            </label>
            <textarea
              value={responseForm.description}
              onChange={(e) =>
                setResponseForm((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              className={`w-full border rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-customGreen ${
                formErrors.description ? "border-red-500" : "border-gray-300"
              }`}
              rows={4}
              placeholder="Enter response description..."
            />
            {formErrors.description && (
              <p className="text-red-500 text-xs mt-1">{formErrors.description}</p>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <button
              onClick={() => {
                setIsResponseModalOpen(false);
                setResponseForm({
                  files: [],
                  description: "",
                  respondingTo: null,
                });
                setFormErrors({});
              }}
              className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 focus:outline-none"
            >
              Cancel
            </button>
            <button
              onClick={handleResponseSubmit}
              disabled={isSubmittingResponse || isDropped}
              className={`px-4 py-2 rounded-md focus:outline-none text-white ${
                isSubmittingResponse || isDropped
                  ? "bg-gray-400 cursor-not-allowed" 
                  : "bg-customGreen hover:bg-green-600"
              }`}
            >
              {isSubmittingResponse ? "Adding..." : "Add Response"}
            </button>
          </div>
        </div>
      </CustomSidebar>
    </div>
  );
};

export default AdvocateAnalysis;